/**
 * ExecutionInputForm — collects the data the grounding gate refused to invent.
 *
 * brainstorm-z8t2.24 (UI half of brainstorm-inv1 / brainstorm-pygp).
 *
 * When a workflow needs data that no node produces and no tool can fetch,
 * roleserver refuses to execute and answers 422:
 *
 *   { error: 'required_inputs_missing',
 *     message: '...',
 *     missing: ['sender_name', 'account_name'],
 *     input_form_schema: { JSON Schema draft-07 } }
 *
 * This renders that schema so the user can answer, then resubmits to the same
 * endpoint as { input_values: {...} }.
 *
 * The schema is read from the REFUSAL, never guessed: the planner invents
 * different key names per run (prospect_name vs prospect_first_name have both
 * been observed), so field names are only knowable from the response.
 *
 * This is NOT an error state. The workflow is fine; it is waiting for data only
 * the user has. Worded and styled accordingly.
 *
 * Deliberately separate from TaskInputForm, which renders a Phase 0 brief form
 * and lets the user remove/rename/reorder fields. Those controls are wrong here:
 * every field shown is one the gate REQUIRES, so removing one client-side just
 * earns another 422.
 */

import React, { useMemo, useState, useCallback } from 'react';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { AlertCircle, Loader, Play, X } from 'lucide-react';
import { tailwindWidgets } from '../widgets';
import {
  buildGateSchema,
  buildGateUiSchema,
  coerceSubmission,
} from './executionInputSchema';

export default function ExecutionInputForm({
  requiredInputs,
  onSubmit,
  onCancel,
  submitting = false,
}) {
  const [formData, setFormData] = useState({});

  const { schema, recordFields } = useMemo(
    () => buildGateSchema(requiredInputs?.input_form_schema, requiredInputs?.missing),
    [requiredInputs]
  );
  const uiSchema = useMemo(() => buildGateUiSchema(schema), [schema]);

  const handleSubmit = useCallback(
    ({ formData: data }) => onSubmit(coerceSubmission(data || {}, recordFields)),
    [onSubmit, recordFields]
  );

  const fieldCount = Object.keys(schema.properties || {}).length;
  if (!requiredInputs || fieldCount === 0) return null;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-3 mb-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <h3 className="font-semibold text-amber-900">
            This workflow needs {fieldCount} more {fieldCount === 1 ? 'detail' : 'details'}
          </h3>
          <p className="text-sm text-amber-800 mt-1">
            {requiredInputs.message ||
              'This workflow needs data it cannot obtain by itself. Supply the required inputs before executing.'}
          </p>
          <p className="text-xs text-amber-700 mt-1">
            It will not guess these values — an invented answer here would be
            wrong everywhere downstream.
          </p>
        </div>
      </div>

      <Form
        schema={schema}
        uiSchema={uiSchema}
        validator={validator}
        widgets={tailwindWidgets}
        formData={formData}
        onChange={({ formData: d }) => setFormData(d)}
        onSubmit={handleSubmit}
        disabled={submitting}
      >
        <div className="flex items-center gap-2 mt-4">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {submitting ? 'Starting…' : 'Save & Run'}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-3 py-2 text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          )}
        </div>
      </Form>
    </div>
  );
}
